import React, { useEffect, useRef, useState } from 'react';
import VideoThumbnail from './VideoThumbnail';
import { getVideoEmbedSrc, getVideoProviderLabel } from '../utils/video';
import './VideoEmbed.css';

// Click-to-play YouTube/Rumble player, shared by the public site and the
// /old reader. Until the viewer presses play only a still image is on the
// page — no third-party script, iframe or cookies — which keeps article
// pages light and lets the poster carry the site's grain treatment.
function VideoEmbed({ video, title, poster = '' }) {
    const videoKey = video ? `${video.provider}:${video.id}` : '';
    // Playing state belongs to one video: switching posts shows the poster.
    const [playingKey, setPlayingKey] = useState('');
    const playing = Boolean(videoKey) && playingKey === videoKey;
    const embedSrc = getVideoEmbedSrc(video, { autoplay: true });
    const iframeRef = useRef(null);

    // Focus follows the press into the player: the focused button is gone,
    // so keyboard and screen-reader users land on the iframe that replaced it.
    useEffect(() => {
        if (playing) {
            iframeRef.current?.focus({ preventScroll: true });
        }
    }, [playing]);

    if (!video?.id || !embedSrc) {
        return null;
    }

    const providerLabel = getVideoProviderLabel(video);
    const label = title ? `Play video: ${title}` : `Play ${providerLabel} video`;

    return (
        <div className={`ds-video-embed${playing ? ' is-playing' : ''}`}>
            {playing ? (
                <iframe
                    ref={iframeRef}
                    src={embedSrc}
                    title={title || `${providerLabel} video`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                />
            ) : (
                <button
                    type="button"
                    className="ds-video-embed-poster grain-wrap"
                    onClick={() => setPlayingKey(videoKey)}
                    aria-label={label}
                >
                    <VideoThumbnail video={video} src={poster} alt="" loading="eager" />
                    <span className="ds-play-badge" aria-hidden="true">▶</span>
                    <span className="ds-video-embed-provider" aria-hidden="true">{providerLabel}</span>
                </button>
            )}
        </div>
    );
}

export default VideoEmbed;
